import { useEffect, useState } from 'react';
import useLocales from 'src/hooks/useLocales';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
// @mui
import {
  Box,
  Card,
  Table,
  Switch,
  Button,
  TableBody,
  Container,
  TableContainer,
  TablePagination,
  FormControlLabel,
} from '@mui/material';

// sections

import { useSnackbar } from 'notistack';
import { observer } from 'mobx-react-lite';
import MyDialog from 'src/components/MyDialog';
import StatusDelete from './CustomerDelete';
import CustomerTableRow from './CustomerTableRow';
import CustomerTableToolbar from './CustomerTableToolbar';
import PermissionBasedGuard from 'src/guards/PermissionBasedGuard';
import { useStore } from 'src/stores/store';
import useTable, { emptyRows, getComparator } from 'src/hooks/useTable';
import useSettings from 'src/hooks/useSettings';
import { PATH_DASHBOARD } from 'src/routes/paths';
import Page from 'src/components/Page';
import HeaderBreadcrumbs from 'src/components/HeaderBreadcrumbs';
import Iconify from 'src/components/Iconify';
import Scrollbar from 'src/components/Scrollbar';
import { TableEmptyRows, TableHeadCustom, TableNoData } from 'src/components/table';
import { ICustomer } from 'src/@types/Choqorak/MainTables/Contract/Customer';
import { storeIdToLocalStorage } from 'src/Common/storeIdToLocalStorage';

// ----------------------------------------------------------------------

export default observer(function CustomerList() {
  const { CustomerStore, ContractInfoStore, RentPaymentStore, ElectricityPaymentStore } = useStore();
  const { GetHistory } = ContractInfoStore;
  const { GetRentPaymentHistory } = RentPaymentStore;
  const { GetElectricityPaymentHistory } = ElectricityPaymentStore;

  const { translate } = useLocales();
  const {
    loadCustomer,
    CustomerList,
    CustomerRegistry,
    totalRecord,
    CustomerSearch,
    getCustomerFromRegistry,
    setOpenCloseDialog,
    openDialog,
    getCustomerForEdit,
    loadCustomerDetail,
    clearSelectedCustomer
  } = CustomerStore;

  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    setPage,
    onSort,
    onChangeDense,
    onChangeRowsPerPage,
  } = useTable();

  const { themeStretch, tablePagination, onChangePagination } = useSettings();

  const [filterName, setFilterName] = useState('');

  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const [CustomerId, setCustomerId] = useState<number>(0);
  const TABLE_HEAD = [
    { id: 'ID', label: `${translate('GeneralFields.Id')}`, align: 'left' },
    { id: 'fullName', label: `${translate('Customer.Name')}`, align: 'left' },
    { id: 'faterName', label: `${translate('Customer.FatherName')}`, align: 'left' },
    { id: 'personalEmail', label: `${translate('Customer.Email')}`, align: 'left' },
    { id: 'phoneNumber', label: `${translate('Customer.PhoneNumber')}`, align: 'left' },

    { id: '', label: `${translate('GeneralFields.Action')}` },
  ];
  const handleFilterName = (filterName: string) => {
    setFilterName(filterName);
    setPage(0);
    if (filterName.length > 1) {
      CustomerSearch({
        pageIndex: 0,
        pageSize: tablePagination,
        searchBy: filterName,
        //dariName: filterName,
      });
    } else if (filterName === '') {
      CustomerSearch({ pageIndex: 0, pageSize: tablePagination });
    }
  };

  const handleOpenConfirm = (id: number) => {
    setOpenCloseDialog();
    setCustomerId(id);
  };

  const handleCloseConfirm = () => {
    setOpenCloseDialog();
  };
  const handleEditRow = (id: number) => {
    getCustomerForEdit(id).then((res) => {
      navigate(PATH_DASHBOARD.Customer.edit);
    });
  };

  const handleCreateUser = (id: number) => {
    getCustomerFromRegistry(id);

    navigate(PATH_DASHBOARD.user.new);
  };

  const handleDetail = (id: number) => {
    storeIdToLocalStorage(id, CustomerStore);
    loadCustomerDetail(id).then(() => {
      navigate(PATH_DASHBOARD.Customer.detail);
    });
  };



  // Customer Detail
  const CustomerDetails = (id: number) => {
    loadCustomerDetail(id).then(() => {
      GetElectricityPaymentHistory({ shopId: id });
      GetRentPaymentHistory({ shopId: id });
      GetHistory({ shopId: id });
      navigate(PATH_DASHBOARD.Customer.detail);
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
    loadCustomer({ pageIndex: newPage, pageSize: tablePagination });
  };

  const handlePageSizeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    onChangePagination(event.target.value);

    onChangeRowsPerPage(event);
    let pageZize = parseInt(event.target.value);
    loadCustomer({ pageIndex: 0, pageSize: pageZize });
  };

  useEffect(() => {
    onChangeRowsPerPage(tablePagination);
    loadCustomer({ pageIndex: 0, pageSize: tablePagination });
  }, [loadCustomer, tablePagination]);

  useEffect(() => {
    if (CustomerRegistry.length <= 1) {
      loadCustomer({ pageIndex: 0, pageSize: rowsPerPage });
    }
  }, []);

  const dataFiltered = applySortFilter({
    tableData: CustomerList,
    comparator: getComparator(order, orderBy),
    filterName: '',
  });

  const denseHeight = dense ? 52 : 72;

  const isNotFound = !dataFiltered.length && !!filterName;

  return (
    <Page title={translate('Customer.Title')}>
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading={translate('Customer.CustomerList')}
          links={[
            { name: `${translate('Department.Dashboard')}`, href: PATH_DASHBOARD.root },

            { name: `${translate('Customer.CustomerList')}` },
          ]}
          action={
            <>
              <PermissionBasedGuard permissions={['Customer-Create']}>
                <Button
                  variant="contained"
                  startIcon={<Iconify icon="eva:plus-fill" />}
                  component={RouterLink}
                  to={PATH_DASHBOARD.Customer.new}
                  onClick={() => {
                    clearSelectedCustomer();
                  }}
                >
                  {translate('CRUD.Create')}
                </Button>
              </PermissionBasedGuard>
            </>
          }
        />

        <Card>
          <CustomerTableToolbar filterName={filterName} onFilterName={handleFilterName} />

          <Scrollbar>
            <TableContainer sx={{ maxHeight: 600, minWidth: 960, position: 'relative' }}>
              <Table stickyHeader size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={totalRecord}
                  onSort={onSort}
                />

                <TableBody>
                  {dataFiltered
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => (
                      <CustomerTableRow
                        key={row.customerId}
                        index={index}
                        row={row}
                        onDeleteRow={() => handleOpenConfirm(row.customerId!)}
                        onEditRow={() => handleEditRow(row.customerId!)}
                        onCreateUser={() => handleCreateUser(row.customerId!)}
                        handleDetail={() => handleDetail(row.customerId!)}
                        doubleClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                          if (e.detail == 2) {
                            CustomerDetails(row.customerId!);
                          }
                        }}
                      />
                    ))}

                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(page, rowsPerPage, totalRecord)}
                  />

                  <TableNoData isNotFound={isNotFound} />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50, 100, 150, 200]}
              component="div"
              labelRowsPerPage={translate('Pagination.RowsPerPage')}
              count={totalRecord}
              labelDisplayedRows={({ from, to, count }) =>
                `${from}-${to} ${translate('Pagination.Of')} ${count}`
              }
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handlePageSizeChange}
            />
            <MyDialog
              open={openDialog}
              onClose={handleCloseConfirm}
              title={translate('CRUD.DeleteTitle')}
              size="md"
            >
              <StatusDelete id={CustomerId} />
            </MyDialog>
            <FormControlLabel
              control={<Switch checked={dense} onChange={onChangeDense} />}
              label={translate('Pagination.Dense')}
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </Card>
      </Container>
    </Page>
  );
});

// ----------------------------------------------------------------------

function applySortFilter({
  tableData,
  comparator,
  filterName,
}: {
  tableData: ICustomer[];
  comparator: (a: any, b: any) => number;
  filterName: string;
}) {
  const stabilizedThis = tableData.map((el, index) => [el, index] as const);

  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  tableData = stabilizedThis.map((el) => el[0]);

  if (filterName) {
    tableData = tableData.filter(
      (item: Record<string, any>) =>
        item.searchBy.toLowerCase().indexOf(filterName.toLowerCase()) !== -1
    );
  }

  return tableData;
}
